import { useRef, useState } from "react";
import { useDbClient, useLiveQuery } from "@tanstack/react-db";
import { ForumPostView } from "@softmax/web-ux/forum";
import type { PostDetailPublic } from "@softmax/observatory-client/types";
import {
  communityNavigation,
  communityAccess,
  usePostImageShare,
} from "@softmax/web-features/community";
import { refreshCollections } from "@softmax/web-features/data";
import { curatedText } from "@softmax/web-features/coworlds";
import { postDetailCollections } from "./post.collections";
import { voteForPost } from "./commands/post.mutations";
import { voteForComment } from "./commands/comment.mutations";
import { useForumVotes } from "./use-forum-votes";
import { WikiRail } from "./forum-wiki-rail";
import { renderLink, renderMarkdown } from "./forum-links";
import { postState } from "./forum-read-state";
import { useForumComments } from "./use-forum-comments";
import type { RoomProps } from "./forum-room.types";

export function ForumPost({
  elevatedPrivileges = false,
  layout = "page",
  navigationSearch = "",
  composeEnabled = false,
  coworld,
  postId,
  viewerUserId,
  displayName,
  description,
  initialDetail = null,
  forumSlug = coworld,
  basePath,
  wikiBasePath,
  home,
}: RoomProps & { postId: string; initialDetail?: PostDetailPublic | null }) {
  const navigation = communityNavigation({
    basePath,
    navigationSearch,
  });
  const db = useDbClient();
  const descriptors = postDetailCollections({
    postId,
    elevatedPrivileges,
    initialDetail,
  });
  const source = db.collection(descriptors.post);
  const query = useLiveQuery((q) =>
    q.from({ post: descriptors.post }).findOne(),
  );
  const comments = useLiveQuery((q) =>
    q
      .from({ comment: descriptors.comments })
      .orderBy(({ comment }) => comment.created_at)
      .orderBy(({ comment }) => comment.id),
  );
  const { accessLost } = communityAccess([query.health, comments.health]);
  const [refreshing, setRefreshing] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const imageShare = usePostImageShare({ cardRef });
  const posts = useForumVotes({
    submit: ({ id, value }) =>
      voteForPost({ db, postId: id, value, elevatedPrivileges, forumSlug }),
  });
  const postVote = posts.votes[postId] ?? ({ value: 0, busy: false } as const);
  const { votes: commentVotes, vote: voteComment } = useForumVotes({
    submit: ({ id, value }) =>
      voteForComment({ db, postId, commentId: id, value, elevatedPrivileges }),
  });
  const {
    busyParentId,
    commentError,
    deletingId,
    deleteError,
    submitComment,
    deleteComment,
  } = useForumComments({ postId, elevatedPrivileges });

  return (
    <ForumPostView
      layout={layout}
      name={curatedText(displayName ?? coworld)}
      description={description ? curatedText(description) : undefined}
      homeHref={home}
      wikiRail={
        layout === "page" ? (
          <WikiRail slug={forumSlug} basePath={wikiBasePath ?? basePath} />
        ) : undefined
      }
      backHref={navigation.forumHref()}
      renderLink={renderLink}
      renderMarkdown={renderMarkdown}
      state={postState({
        query,
        comments,
        accessLost,
        refreshing,
        postVote,
        commentVotes,
        viewerUserId,
        elevatedPrivileges,
      })}
      onRefresh={() => {
        setRefreshing(true);
        const request = refreshCollections({
          signal: db.requireDependency<AbortController>("lifetime").signal,
          queries: [query.collection, comments.collection],
          refresh: () => source.utils.refetch({ throwOnError: true }),
        });
        void request.finally(() => setRefreshing(false));
      }}
      postVote={postVote}
      commentVotes={commentVotes}
      onPostVote={(direction) => posts.vote({ id: postId, direction })}
      shareImage={
        elevatedPrivileges && !accessLost
          ? {
              cardRef,
              status: imageShare.status,
              error: imageShare.error,
              onShare: imageShare.share,
            }
          : undefined
      }
      onCommentVote={({ commentId, direction }) =>
        voteComment({ id: commentId, direction })
      }
      deleteComment={
        viewerUserId && !accessLost
          ? { busyId: deletingId, error: deleteError, onDelete: deleteComment }
          : undefined
      }
      compose={
        composeEnabled && !accessLost
          ? { busyParentId, error: commentError, onSubmit: submitComment }
          : undefined
      }
    />
  );
}
